import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Receipt, Users, FileText, History, Box, UserCog, DollarSign, Image, Building2, AlertTriangle, Wand2, Upload, Tag, Package, ShoppingBag, Mail, Menu, ChevronDown, Settings, MessageCircle, Briefcase, Smartphone, RotateCcw, Merge, Ticket } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import WhatsAppButton from "./WhatsAppButton";
import ChatWidget from "./ChatWidget";
import { getWhatsAppOpenMode, setWhatsAppOpenMode } from "../utils/whatsappLink";
import ExpensesDailyAlert from "./ExpensesDailyAlert";
import { BoxContext, useBox } from "./BoxContext";
import { MobileNav } from "./MobileNav";
import { MobileBottomBar } from "./MobileBottomBar";

// Re-export so existing imports (`import { useBox } from "./Layout"`) keep working.
export { BoxContext, useBox };

export default function Layout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [expenseAlertCount, setExpenseAlertCount] = useState(0); // total dueSoon + dueTodayOrOverdue
  const expenseToastShownRef = useRef(false);
  const [sinpeUnsentCount, setSinpeUnsentCount] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const pendingOrdersPrevRef = useRef(null);

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [whatsappMode, setWhatsappMode] = useState(getWhatsAppOpenMode());

  const saveSettings = () => {
    setWhatsAppOpenMode(whatsappMode);
    toast.success(`WhatsApp se abrirá ${whatsappMode === "web" ? "en el navegador (web)" : "con la app instalada"}`);
    setSettingsOpen(false);
  };

  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        const response = await axiosInstance.get('/boxes');
        setBoxes(response.data);
        
        // Auto-select first box or assigned box
        if (response.data.length > 0) {
          if (user.assigned_box_id) {
            const assignedBox = response.data.find(b => b.id === user.assigned_box_id);
            setSelectedBox(assignedBox || response.data[0]);
          } else {
            setSelectedBox(response.data[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching boxes:", error);
      }
    };

    fetchBoxes();
  }, [user]);

  // Check expense reminders on system entry (login).
  // - Shows "vence pronto" (dueMañana) and "vencido/hoy" toasts ONCE per session.
  // - Refreshes badge count periodically so it stays accurate while the user is in the app.
  useEffect(() => {
    if (!selectedBox || !user) return;

    const fetchExpenseAlerts = async (showToasts) => {
      try {
        // Always scope alerts to the currently selected box. The backend forces
        // the cashier to their assigned_box_id regardless, but passing it
        // explicitly keeps the contract unambiguous and lets Master switch boxes.
        const resp = await axiosInstance.get(`/expenses?box_id=${selectedBox.id}&status=pendiente`);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const startOfDay = (d) => {
          const x = new Date(d);
          x.setHours(0, 0, 0, 0);
          return x;
        };

        const dueSoon = [];
        const dueTodayOrOverdue = [];

        (resp.data || []).forEach(exp => {
          const dueDate = startOfDay(exp.due_date);
          if (dueDate.getTime() === tomorrow.getTime()) {
            dueSoon.push(exp);
          } else if (dueDate.getTime() <= today.getTime()) {
            dueTodayOrOverdue.push(exp);
          }
        });

        setExpenseAlertCount(dueSoon.length + dueTodayOrOverdue.length);

        if (showToasts) {
          if (dueSoon.length > 0) {
            const list = dueSoon.map(e => `${e.recipient}: ₡${(e.amount_colones || 0).toLocaleString()}`).join(', ');
            toast.warning(`🔔 RECIBO VENCE PRONTO (${dueSoon.length}): ${list}`, {
              duration: window.innerWidth < 768 ? 5000 : 9000,
              style: {
                background: '#FEF3C7',
                color: '#92400E',
                border: '2px solid #F59E0B'
              }
            });
          }
          if (dueTodayOrOverdue.length > 0) {
            const list = dueTodayOrOverdue.map(e => {
              const dueDate = startOfDay(e.due_date);
              const tag = dueDate.getTime() === today.getTime() ? 'HOY' : 'VENCIDO';
              return `${e.recipient} (${tag}): ₡${(e.amount_colones || 0).toLocaleString()}`;
            }).join(', ');
            toast.error(`⚠️ GASTOS POR PAGAR (${dueTodayOrOverdue.length}): ${list}`, {
              duration: window.innerWidth < 768 ? 5000 : 11000,
              style: {
                background: '#FEE2E2',
                color: '#991B1B',
                border: '2px solid #EF4444'
              }
            });
          }
        }
      } catch (error) {
        console.error("Error fetching expense alerts:", error);
      }
    };

    // Show toasts only the first time the user enters the system in this session.
    // On mobile: once per login (survives reloads) and shorter duration.
    const isMobile = window.innerWidth < 768;
    const loginKey = `palistore-exp-toasts-${localStorage.getItem('token')}`;
    const shouldShowToasts = !expenseToastShownRef.current && !(isMobile && localStorage.getItem(loginKey));
    if (shouldShowToasts) {
      expenseToastShownRef.current = true;
      if (isMobile) localStorage.setItem(loginKey, "1");
    }
    fetchExpenseAlerts(shouldShowToasts);

    // Refresh badge count every 5 minutes to keep it current while session is active.
    const interval = setInterval(() => fetchExpenseAlerts(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedBox, user]);

  // Poll SINPE unsent count for the navbar badge. Lightweight: reads from
  // cache (sinpe_verifications + sinpe_sent), no IMAP hit. Refreshes every
  // 30 seconds so the user can see new pending SINPE from anywhere in the app.
  useEffect(() => {
    if (!selectedBox || !user) return;
    let cancelled = false;

    // Only poll when SINPE module is enabled for this box AND user can access it
    const sinpeEnabled = selectedBox?.enabled_modules ? selectedBox.enabled_modules.sinpe !== false : true;
    const canSeeSinpe = sinpeEnabled && (user.role === "master" || (user.role === "cajero" && selectedBox?.sinpe_email));
    if (!canSeeSinpe) { setSinpeUnsentCount(0); return; }

    const fetchUnsent = async () => {
      try {
        const resp = await axiosInstance.get(`/sinpe/unsent-count?box_id=${selectedBox.id}`);
        if (!cancelled) setSinpeUnsentCount(resp.data?.count || 0);
      } catch (e) {
        if (!cancelled) setSinpeUnsentCount(0);
      }
    };
    fetchUnsent();
    const id = setInterval(fetchUnsent, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedBox, user]);

  // Poll online-store pending orders. When the count goes UP between polls
  // (i.e. a new order arrived), beep once and animate the badge. Lightweight:
  // a single GET every 30s, and the sound is generated in-browser (no asset).
  useEffect(() => {
    if (!selectedBox || !user) return;
    let cancelled = false;

    // Best-effort beep using Web Audio API. Silent failure if blocked.
    const beep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        o.start();
        // Second tone for a "ding-ding" effect
        setTimeout(() => { try { o.frequency.value = 1320; } catch (_) { /* noop */ } }, 180);
        o.stop(ctx.currentTime + 0.5);
        setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 800);
      } catch (_) { /* ignore */ }
    };

    const fetchPending = async () => {
      try {
        const r = await axiosInstance.get(`/orders?box_id=${selectedBox.id}&status=pending`);
        const count = r.data?.pending_count ?? (r.data?.orders || []).length;
        if (cancelled) return;
        const prev = pendingOrdersPrevRef.current;
        if (prev !== null && count > prev) {
          beep();
          toast.success(`¡Nuevo pedido en línea! Tienes ${count} pendiente${count > 1 ? "s" : ""}.`, { duration: 6000 });
        }
        pendingOrdersPrevRef.current = count;
        setPendingOrdersCount(count);
      } catch (_) { /* silent */ }
    };
    fetchPending();
    const id = setInterval(fetchPending, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedBox, user]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Helper: check if a module is enabled for the currently selected box.
  // If enabled_modules is undefined or the key is missing, defaults to enabled (true).
  const isModuleEnabled = (moduleKey) => {
    const em = selectedBox?.enabled_modules;
    if (!em) return true;
    return em[moduleKey] !== false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="px-2 sm:px-4 lg:px-6 py-3 max-w-[1800px] mx-auto w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 lg:space-x-6">
              <h1 className="text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                PaliStore
              </h1>
              
              <div className="hidden md:flex flex-nowrap overflow-x-auto no-scrollbar gap-1 max-w-[calc(100vw-380px)] items-center [&>a]:shrink-0 [&>button]:shrink-0">
                <Link to="/">
                  <Button
                    data-testid="nav-dashboard-link"
                    variant={isActive('/') ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>

                {/* Dropdown menu agrupando los items secundarios */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="nav-menu-dropdown-trigger"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3 relative"
                    >
                      <Menu className="w-4 h-4" />
                      Menú
                      <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                      {sinpeUnsentCount > 0 && (
                        <span
                          data-testid="menu-sinpe-alert-badge"
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm border border-white animate-pulse"
                          title={`${sinpeUnsentCount} SINPE pendiente(s) de enviar`}
                        >
                          {sinpeUnsentCount > 99 ? '99+' : sinpeUnsentCount}
                        </span>
                      )}
                      {sinpeUnsentCount === 0 && pendingOrdersCount > 0 && (
                        <span
                          data-testid="menu-orders-alert-badge"
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm border border-white animate-pulse"
                          title={`${pendingOrdersCount} pedido(s) en línea pendiente(s)`}
                        >
                          {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Navegación</DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    <DropdownMenuItem asChild>
                      <Link to="/transactions" data-testid="nav-transactions-link" className="cursor-pointer w-full">
                        <Receipt className="w-4 h-4 mr-2" />
                        Movimientos
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/customers" data-testid="nav-customers-link" className="cursor-pointer w-full">
                        <Users className="w-4 h-4 mr-2" />
                        Clientes
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/debts" data-testid="nav-debts-link" className="cursor-pointer w-full">
                        <FileText className="w-4 h-4 mr-2" />
                        Cuentas
                      </Link>
                    </DropdownMenuItem>

                    {isModuleEnabled('inventory') && (
                      <DropdownMenuItem asChild>
                        <Link to="/inventory" data-testid="nav-inventory-link" className="cursor-pointer w-full">
                          <Package className="w-4 h-4 mr-2" />
                          Inventario
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {(user?.is_super_master || selectedBox?.lottery_accessible) && (
                      <DropdownMenuItem asChild>
                        <Link to="/apuestas" data-testid="nav-apuestas-link" className="cursor-pointer w-full">
                          <Ticket className="w-4 h-4 mr-2" />
                          Apuestas y Sorteos
                        </Link>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem asChild>
                      <Link to="/orders" data-testid="nav-orders-link" className="cursor-pointer w-full">
                        <Package className="w-4 h-4 mr-2" />
                        Pedidos en línea
                        {pendingOrdersCount > 0 && (
                          <span
                            data-testid="menu-orders-count-badge"
                            className="ml-auto min-w-[20px] h-[20px] px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                          >
                            {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/suppliers" data-testid="nav-suppliers-link" className="cursor-pointer w-full">
                        <Package className="w-4 h-4 mr-2" />
                        Proveedores y Facturas
                      </Link>
                    </DropdownMenuItem>

                    {isModuleEnabled('layaways') && (
                      <DropdownMenuItem asChild>
                        <Link to="/layaways" data-testid="nav-layaways-link" className="cursor-pointer w-full">
                          <ShoppingBag className="w-4 h-4 mr-2" />
                          Apartados
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {user?.role === 'master' && isModuleEnabled('sinpe') && (
                      <DropdownMenuItem asChild>
                        <Link to="/sinpe-verify" data-testid="nav-sinpe-verify-link" className="cursor-pointer w-full flex items-center justify-between">
                          <span className="flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            SINPE
                          </span>
                          {sinpeUnsentCount > 0 && (
                            <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                              {sinpeUnsentCount > 99 ? '99+' : sinpeUnsentCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user?.role === 'cajero' && selectedBox?.sinpe_email && isModuleEnabled('sinpe') && (
                      <DropdownMenuItem asChild>
                        <Link to="/sinpe-verify" data-testid="nav-sinpe-verify-link" className="cursor-pointer w-full flex items-center justify-between">
                          <span className="flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            SINPE
                          </span>
                          {sinpeUnsentCount > 0 && (
                            <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                              {sinpeUnsentCount > 99 ? '99+' : sinpeUnsentCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {user.role === 'cajero' && isModuleEnabled('sublimation') && (
                      <DropdownMenuItem asChild>
                        <Link to="/sublimacion" data-testid="nav-sublimacion-link" className="cursor-pointer w-full">
                          <Image className="w-4 h-4 mr-2" />
                          Sublimación
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {user.role === 'cajero' && isModuleEnabled('arts') && (
                      <DropdownMenuItem asChild>
                        <Link to="/arts" data-testid="nav-arts-link" className="cursor-pointer w-full">
                          <Wand2 className="w-4 h-4 mr-2" />
                          Artes IA
                        </Link>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem asChild>
                      <Link to="/reports" data-testid="nav-reports-link" className="cursor-pointer w-full">
                        <History className="w-4 h-4 mr-2" />
                        Reportes
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/sinpe-log" data-testid="nav-sinpe-log-link" className="cursor-pointer w-full">
                        <Smartphone className="w-4 h-4 mr-2" />
                        Registro SINPE
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/credit-notes-report" data-testid="nav-credit-notes-link" className="cursor-pointer w-full">
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Notas de Crédito
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/customers-dedupe" data-testid="nav-dedupe-link" className="cursor-pointer w-full">
                        <Merge className="w-4 h-4 mr-2" />
                        Deduplicar Clientes
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/import" data-testid="nav-import-link" className="cursor-pointer w-full">
                        <Upload className="w-4 h-4 mr-2" />
                        Importar
                      </Link>
                    </DropdownMenuItem>

                    {isModuleEnabled('labels') && (
                      <DropdownMenuItem asChild>
                        <Link to="/labels" data-testid="nav-labels-link" className="cursor-pointer w-full">
                          <Tag className="w-4 h-4 mr-2" />
                          Etiquetas
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {user.role === 'master' && (
                      <>
                        <DropdownMenuSeparator />
                        {user.is_super_master && (
                          <DropdownMenuItem asChild>
                            <Link to="/lottery-access" data-testid="nav-lottery-access-link" className="cursor-pointer w-full">
                              <Ticket className="w-4 h-4 mr-2" />
                              Acceso a Apuestas
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link to="/hr" data-testid="nav-hr-link" className="cursor-pointer w-full">
                            <Briefcase className="w-4 h-4 mr-2" />
                            Recursos Humanos
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild>
                          <Link to="/consolidated" data-testid="nav-consolidated-link" className="cursor-pointer w-full">
                            <FileText className="w-4 h-4 mr-2" />
                            Consolidado
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild>
                          <Link to="/emergency" data-testid="nav-emergency-link" className="cursor-pointer w-full text-red-600 focus:text-red-700">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            🚨 Emergencia
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Items que SIEMPRE quedan visibles (solo Master) */}
                {user.role === 'master' && (
                  <>
                    <Link to="/boxes">
                      <Button
                        data-testid="nav-boxes-link"
                        variant={isActive('/boxes') ? 'default' : 'ghost'}
                        size="sm"
                        className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3"
                      >
                        <Box className="w-4 h-4" />
                        Cajas
                      </Button>
                    </Link>

                    <Link to="/banks">
                      <Button
                        data-testid="nav-banks-link"
                        variant={isActive('/banks') ? 'default' : 'ghost'}
                        size="sm"
                        className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3"
                      >
                        <Building2 className="w-4 h-4" />
                        Bancos
                      </Button>
                    </Link>

                    {user.is_super_master && (
                      <Link to="/users">
                        <Button
                          data-testid="nav-users-link"
                          variant={isActive('/users') ? 'default' : 'ghost'}
                          size="sm"
                          className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3"
                        >
                          <UserCog className="w-4 h-4" />
                          Usuarios
                        </Button>
                      </Link>
                    )}
                  </>
                )}

                {/* Gastos: visible para Master y Cajero (cada caja maneja sus gastos) */}
                <Link to="/expenses">
                  <Button
                    data-testid="nav-expenses-link"
                    variant={isActive('/expenses') ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-1.5 text-xs lg:text-sm px-2 lg:px-3 relative"
                  >
                    <DollarSign className="w-4 h-4" />
                    Gastos
                    {expenseAlertCount > 0 && (
                      <span
                        data-testid="expenses-alert-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm border border-white animate-pulse"
                        title={`${expenseAlertCount} gasto(s) por vencer o vencidos`}
                      >
                        {expenseAlertCount > 99 ? '99+' : expenseAlertCount}
                      </span>
                    )}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              {/* Box Selector */}
              {boxes.length > 0 && (
                <div className="hidden md:flex items-center gap-2">
                  <Box className="w-4 h-4 text-slate-600" />
                  <Select
                    value={selectedBox?.id}
                    onValueChange={(value) => {
                      const box = boxes.find(b => b.id === value);
                      setSelectedBox(box);
                    }}
                    disabled={user.role === 'cajero' && user.assigned_box_id}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="box-selector-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {boxes.map(box => (
                        <SelectItem key={box.id} value={box.id} data-testid={`box-option-${box.name}`}>
                          {box.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-slate-800">{user.username}</p>
                <p className={`text-xs px-2 py-0.5 rounded-full inline-block ${
                  user.role === 'master' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {user.role === 'master' ? 'Master' : 'Cajero'}
                </p>
              </div>
              
              <Button
                data-testid="settings-button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setWhatsappMode(getWhatsAppOpenMode());
                  setSettingsOpen(true);
                }}
                title="Configuración"
                className="hover:bg-slate-100 hidden md:inline-flex"
              >
                <Settings className="w-4 h-4" />
              </Button>

              <ChatWidget user={user} selectedBox={selectedBox} inHeader={true} />

              <Button
                data-testid="logout-button"
                variant="outline"
                onClick={handleLogout}
                className="hover:bg-red-50 hover:border-red-300 gap-2 hidden md:inline-flex"
              >
                <LogOut className="w-4 h-4" />
                <span>Cerrar Sesión</span>
              </Button>

              <MobileNav
                user={user}
                boxes={boxes}
                selectedBox={selectedBox}
                setSelectedBox={setSelectedBox}
                isModuleEnabled={isModuleEnabled}
                sinpeUnsentCount={sinpeUnsentCount}
                pendingOrdersCount={pendingOrdersCount}
                expenseAlertCount={expenseAlertCount}
                onLogout={handleLogout}
                onSettings={() => { setWhatsappMode(getWhatsAppOpenMode()); setSettingsOpen(true); }}
              />
            </div>
          </div>
        </div>
      </nav>

      <main className="px-2 sm:px-4 lg:px-6 pt-8 pb-24 md:pb-8 max-w-[1800px] mx-auto w-full">
        <BoxContext.Provider value={{ selectedBox, boxes }}>
          <ExpensesDailyAlert user={user} />
          <Outlet />
        </BoxContext.Provider>
      </main>

      {/* WhatsApp Floating Button */}
      <div className={location.pathname === '/inventory' ? 'hidden md:block' : ''}>
        <WhatsAppButton whatsappNumber={selectedBox?.whatsapp_number} />
      </div>

      <MobileBottomBar expenseAlertCount={expenseAlertCount} boxId={selectedBox?.id} showPrice={isModuleEnabled("inventory")} />

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración
            </DialogTitle>
            <DialogDescription>
              Preferencias guardadas en este dispositivo (no afectan a otros usuarios).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-green-600" />
                <p className="font-semibold text-slate-800">Cómo abrir WhatsApp</p>
              </div>

              <div className="space-y-2">
                <label
                  data-testid="whatsapp-mode-app"
                  className={`flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition ${
                    whatsappMode === "app"
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="whatsapp_mode"
                    value="app"
                    checked={whatsappMode === "app"}
                    onChange={() => setWhatsappMode("app")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">App instalada (recomendado)</p>
                    <p className="text-xs text-slate-600">
                      Abre la app de WhatsApp Desktop directamente. Requiere tenerla instalada.
                    </p>
                  </div>
                </label>

                <label
                  data-testid="whatsapp-mode-web"
                  className={`flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition ${
                    whatsappMode === "web"
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="whatsapp_mode"
                    value="web"
                    checked={whatsappMode === "web"}
                    onChange={() => setWhatsappMode("web")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">Navegador (WhatsApp Web)</p>
                    <p className="text-xs text-slate-600">
                      Abre wa.me en una pestaña nueva. Útil si no tenés la app instalada.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
            <Button
              data-testid="settings-save-btn"
              onClick={saveSettings}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}